import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	Checkbox,
	CheckboxOption,
	DatePicker,
	FileDropzone,
	FileUploadCard,
	Form, FormControl, FormField, FormItem, FormLabel,
	Input,
	Label,
	RadioGroup, RadioOption,
	SectionHeading,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Tabs, TabsList, TabsTrigger,
	Textarea,
} from '@rentflowdev/ui'
import {
	IMPROVEMENTS_FATES, RENT_TERM_KINDS, RETURN_CONDITIONS, TRANSFER_MODES,
	type ImprovementsFate,
	type RentTermKind,
	type ReturnCondition,
	type TermsAttachment,
	type TermsValues,
	type TransferMode,
	type TransferMoment,
} from './types'

const termsSchema = z.object({
	rentTermKind:          z.enum(RENT_TERM_KINDS as [RentTermKind, ...RentTermKind[]]),
	contractFromIso:       z.string(),
	contractToIso:         z.string(),
	effectiveFromIso:      z.string(),
	effectiveMatchesStart: z.boolean(),

	landlordNoticeCount:   z.number().min(0),
	landlordNoticeUnit:    z.string(),
	tenantNoticeCount:     z.number().min(0),
	tenantNoticeUnit:      z.string(),

	transferMode:                z.enum(TRANSFER_MODES as [TransferMode, ...TransferMode[]]),
	transferActCount:            z.number().min(0),
	transferActUnit:             z.string(),
	transferActTiming:           z.string(),
	transferMoment:              z.enum(['onSigning', 'onActualTransfer']),
	transferExecutorSignatoryId: z.string(),
	transferCustomerSignatoryId: z.string(),

	returnMode:       z.enum(TRANSFER_MODES as [TransferMode, ...TransferMode[]]),
	returnCount:      z.number().min(0),
	returnUnit:       z.string(),
	returnTiming:     z.string(),

	improvementsFate:    z.enum(IMPROVEMENTS_FATES as [ImprovementsFate, ...ImprovementsFate[]]),
	improvementsConsent: z.string(),

	otherConditions: z.string(),
})

type TermsFormSchema = z.infer<typeof termsSchema>

// Reference options — stubbed until backend #63 ships. Keys mirror expected enum values,
// labels come from i18n so RU/EN stay in sync with the design.
const NOTICE_UNIT_KEYS = ['months', 'days'] as const
const TIMING_KEYS      = ['afterContractEnd', 'onAgreedDate'] as const
const SIGNATORY_STUBS  = [
	{ id: 'sig-as-contract', i18nKey: 'asInContract' },
] as const
const CONSENT_STUBS = [
	{ id: 'consent-written', i18nKey: 'written' },
] as const

const isoToDate = (iso?: string): Date | undefined => (iso ? new Date(iso) : undefined)
const dateToIso = (d?: Date): string                => (d ? d.toISOString().slice(0, 10) : '')

function parseIntSafe(raw: string): number {
	const n = parseInt(raw, 10)
	return Number.isFinite(n) ? n : 0
}

interface StepTermsProps {
	initial: TermsValues
	formId:  string
	onValid: (values: TermsValues) => void
}

export function StepTerms({ initial, formId, onValid }: StepTermsProps) {
	const { t } = useTranslation()

	const form = useForm<TermsFormSchema>({
		resolver: zodResolver(termsSchema),
		defaultValues: {
			rentTermKind:          initial.rentTermKind,
			contractFromIso:       initial.contractFromIso,
			contractToIso:         initial.contractToIso,
			effectiveFromIso:      initial.effectiveFromIso,
			effectiveMatchesStart: initial.effectiveMatchesStart,

			landlordNoticeCount:   initial.landlordNoticeCount,
			landlordNoticeUnit:    initial.landlordNoticeUnit,
			tenantNoticeCount:     initial.tenantNoticeCount,
			tenantNoticeUnit:      initial.tenantNoticeUnit,

			transferMode:                initial.transferMode,
			transferActCount:            initial.transferActCount,
			transferActUnit:             initial.transferActUnit,
			transferActTiming:           initial.transferActTiming,
			transferMoment:              initial.transferMoment,
			transferExecutorSignatoryId: initial.transferExecutorSignatoryId,
			transferCustomerSignatoryId: initial.transferCustomerSignatoryId,

			returnMode:       initial.returnMode,
			returnCount:      initial.returnCount,
			returnUnit:       initial.returnUnit,
			returnTiming:     initial.returnTiming,

			improvementsFate:    initial.improvementsFate,
			improvementsConsent: initial.improvementsConsent,

			otherConditions: initial.otherConditions,
		},
		mode: 'onChange',
	})

	// Checkboxes + uploaded files live outside RHF — they're list-shaped and easier
	// to manage as local state, collected into `onValid` on submit.
	const [conditions, setConditions]   = useState<ReturnCondition[]>(initial.returnConditions)
	const [attachments, setAttachments] = useState<TermsAttachment[]>(initial.attachments)

	function toggleCondition(value: ReturnCondition, checked: boolean) {
		setConditions((prev) => (checked
			? Array.from(new Set([...prev, value]))
			: prev.filter((c) => c !== value)))
	}

	function handleFilesSelected(files: FileList) {
		const additions: TermsAttachment[] = Array.from(files).map((f) => ({
			id:        crypto.randomUUID(),
			name:      f.name,
			sizeLabel: `${(f.size / 1024).toFixed(1)} КБ`,
			state:     'uploaded',
		}))
		setAttachments((prev) => [...prev, ...additions])
	}

	function removeAttachment(id: string) {
		setAttachments((prev) => prev.filter((a) => a.id !== id))
	}

	function handleSubmit(values: TermsFormSchema) {
		onValid({
			...values,
			returnConditions: conditions,
			attachments,
		})
	}

	const rentTermKind = form.watch('rentTermKind')
	const transferMode = form.watch('transferMode')
	const returnMode   = form.watch('returnMode')
	const effectiveMatchesStart = form.watch('effectiveMatchesStart')
	const contractFromIso       = form.watch('contractFromIso')

	// Пока даты связаны, вступление следует за началом — в том числе когда
	// начало меняют после того, как галочку поставили.
	useEffect(() => {
		if (effectiveMatchesStart) form.setValue('effectiveFromIso', contractFromIso)
	}, [effectiveMatchesStart, contractFromIso, form])

	return (
		<Form {...form}>
			<form
				id={formId}
				onSubmit={form.handleSubmit(handleSubmit)}
				className="flex flex-col gap-6"
			>
				{/* ── Срок аренды ────────────────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.rentTerm')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="rentTermKind"
						render={({ field }) => (
							<Tabs value={field.value} onValueChange={(v) => field.onChange(v as RentTermKind)}>
								<TabsList variant="pills">
									<TabsTrigger variant="pills" value="determined">
										{t('deals.documents.wizard.terms.kindDetermined')}
									</TabsTrigger>
									<TabsTrigger variant="pills" value="indeterminate">
										{t('deals.documents.wizard.terms.kindIndeterminate')}
									</TabsTrigger>
								</TabsList>
							</Tabs>
						)}
					/>
					{rentTermKind === 'determined' && (
						<FormItem>
							<FormLabel>{t('deals.documents.wizard.terms.contractRange')}</FormLabel>
							<div className="flex items-center">
								<FormField
									control={form.control}
									name="contractFromIso"
									render={({ field }) => (
										<FormControl>
											<DatePicker
												value={isoToDate(field.value as string | undefined)}
												onChange={(d) => field.onChange(dateToIso(d))}
												className="flex-1"
											/>
										</FormControl>
									)}
								/>
								<span className="w-8 h-[42px] flex items-center justify-center text-sm leading-5 text-(--color-muted-foreground)">—</span>
								<FormField
									control={form.control}
									name="contractToIso"
									render={({ field }) => (
										<FormControl>
											<DatePicker
												value={isoToDate(field.value as string | undefined)}
												onChange={(d) => field.onChange(dateToIso(d))}
												className="flex-1"
											/>
										</FormControl>
									)}
								/>
							</div>
						</FormItem>
					)}
					<FormField
						control={form.control}
						name="effectiveFromIso"
						render={({ field }) => (
							<FormItem className="w-1/2 pr-1">
								<FormLabel>{t('deals.documents.wizard.terms.effectiveFrom')}</FormLabel>
								<FormControl>
									<DatePicker
										value={isoToDate(field.value as string | undefined)}
										onChange={(d) => field.onChange(dateToIso(d))}
										disabled={effectiveMatchesStart}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="effectiveMatchesStart"
						render={({ field }) => (
							<Label
								htmlFor="effective-matches-start"
								className="flex items-center gap-2 cursor-pointer"
							>
								<Checkbox
									id="effective-matches-start"
									checked={field.value}
									onCheckedChange={(c) => {
										const matches = c === true
										field.onChange(matches)
										// Галочка обещала, что дата вступления совпадает с датой
										// начала, и не делала ничего: её никто не читал. Теперь
										// она эту дату и подставляет.
										if (matches) form.setValue('effectiveFromIso', form.getValues('contractFromIso'))
									}}
								/>
								<span className="text-sm leading-none font-[500] text-(--color-foreground)">
									{t('deals.documents.wizard.terms.effectiveMatchesStart')}
								</span>
							</Label>
						)}
					/>
				</section>

				{/* ── Сроки уведомления о расторжении ────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.noticeTitle')}
					</SectionHeading>
					<div className="flex gap-2">
						<FormItem className="flex-1">
							<FormLabel>{t('deals.documents.wizard.terms.noticeLandlord')}</FormLabel>
							<div className="flex gap-2">
								<FormField
									control={form.control}
									name="landlordNoticeCount"
									render={({ field }) => (
										<FormControl>
											<Input
												type="number"
												inputMode="numeric"
												value={String(field.value)}
												onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
												className="w-20"
											/>
										</FormControl>
									)}
								/>
								<FormField
									control={form.control}
									name="landlordNoticeUnit"
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger size="md" className="flex-1">
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{NOTICE_UNIT_KEYS.map((k) => (
													<SelectItem key={k} value={k}>
														{t(`deals.documents.wizard.terms.unit_${k}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</div>
						</FormItem>
						<FormItem className="flex-1">
							<FormLabel>{t('deals.documents.wizard.terms.noticeTenant')}</FormLabel>
							<div className="flex gap-2">
								<FormField
									control={form.control}
									name="tenantNoticeCount"
									render={({ field }) => (
										<FormControl>
											<Input
												type="number"
												inputMode="numeric"
												value={String(field.value)}
												onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
												className="w-20"
											/>
										</FormControl>
									)}
								/>
								<FormField
									control={form.control}
									name="tenantNoticeUnit"
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger size="md" className="flex-1">
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{NOTICE_UNIT_KEYS.map((k) => (
													<SelectItem key={k} value={k}>
														{t(`deals.documents.wizard.terms.unit_${k}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</div>
						</FormItem>
					</div>
				</section>

				{/* ── Передача помещения ─────────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.transferTitle')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="transferMode"
						render={({ field }) => (
							<Tabs value={field.value} onValueChange={(v) => field.onChange(v as TransferMode)}>
								<TabsList variant="pills">
									<TabsTrigger variant="pills" value="act">
										{t('deals.documents.wizard.terms.modeAct')}
									</TabsTrigger>
									<TabsTrigger variant="pills" value="noAct">
										{t('deals.documents.wizard.terms.modeNoAct')}
									</TabsTrigger>
								</TabsList>
							</Tabs>
						)}
					/>

					{transferMode === 'act' && (
						<>
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.terms.transferActTerm')}</FormLabel>
								<div className="flex gap-2">
									<FormField
										control={form.control}
										name="transferActCount"
										render={({ field }) => (
											<FormControl>
												<Input
													type="number"
													inputMode="numeric"
													value={String(field.value)}
													onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
													className="w-20"
												/>
											</FormControl>
										)}
									/>
									<FormField
										control={form.control}
										name="transferActUnit"
										render={({ field }) => (
											<Select value={field.value} onValueChange={field.onChange}>
												<FormControl>
													<SelectTrigger size="md" className="flex-1">
														<SelectValue />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{NOTICE_UNIT_KEYS.map((k) => (
														<SelectItem key={k} value={k}>
															{t(`deals.documents.wizard.terms.unit_${k}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
									<FormField
										control={form.control}
										name="transferActTiming"
										render={({ field }) => (
											<Select value={field.value} onValueChange={field.onChange}>
												<FormControl>
													<SelectTrigger size="md" className="flex-1">
														<SelectValue placeholder={t('deals.documents.wizard.terms.timing_afterContractEnd')} />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{TIMING_KEYS.map((k) => (
														<SelectItem key={k} value={k}>
															{t(`deals.documents.wizard.terms.timing_${k}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
								</div>
							</FormItem>

							<FormItem>
								<FormLabel>{t('deals.documents.wizard.terms.transferMoment')}</FormLabel>
								<FormField
									control={form.control}
									name="transferMoment"
									render={({ field }) => (
										<RadioGroup
											value={field.value}
											onValueChange={(v) => field.onChange(v as TransferMoment)}
											className="flex flex-wrap gap-4"
										>
											<RadioOption
												value="onSigning"
												id="transfer-moment-signing"
												bordered={false}
												label={t('deals.documents.wizard.terms.transferMoment_onSigning')}
											/>
											<RadioOption
												value="onActualTransfer"
												id="transfer-moment-actual"
												bordered={false}
												label={t('deals.documents.wizard.terms.transferMoment_onActualTransfer')}
											/>
										</RadioGroup>
									)}
								/>
							</FormItem>

							<FormField
								control={form.control}
								name="transferExecutorSignatoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('deals.documents.wizard.terms.transferExecutor')}</FormLabel>
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t('deals.documents.wizard.terms.signatory_asInContract')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{SIGNATORY_STUBS.map((s) => (
													<SelectItem key={s.id} value={s.id}>
														{t(`deals.documents.wizard.terms.signatory_${s.i18nKey}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="transferCustomerSignatoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('deals.documents.wizard.terms.transferCustomer')}</FormLabel>
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t('deals.documents.wizard.terms.signatory_asInContract')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{SIGNATORY_STUBS.map((s) => (
													<SelectItem key={s.id} value={s.id}>
														{t(`deals.documents.wizard.terms.signatory_${s.i18nKey}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>
						</>
					)}
				</section>

				{/* ── Возврат помещения ──────────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.returnTitle')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="returnMode"
						render={({ field }) => (
							<Tabs value={field.value} onValueChange={(v) => field.onChange(v as TransferMode)}>
								<TabsList variant="pills">
									<TabsTrigger variant="pills" value="act">
										{t('deals.documents.wizard.terms.modeAct')}
									</TabsTrigger>
									<TabsTrigger variant="pills" value="noAct">
										{t('deals.documents.wizard.terms.modeNoAct')}
									</TabsTrigger>
								</TabsList>
							</Tabs>
						)}
					/>

					{returnMode === 'act' && (
						<FormItem>
							<FormLabel>{t('deals.documents.wizard.terms.returnTerm')}</FormLabel>
							<div className="flex gap-2">
								<FormField
									control={form.control}
									name="returnCount"
									render={({ field }) => (
										<FormControl>
											<Input
												type="number"
												inputMode="numeric"
												value={String(field.value)}
												onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
												className="w-20"
											/>
										</FormControl>
									)}
								/>
								<FormField
									control={form.control}
									name="returnUnit"
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger size="md" className="flex-1">
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{NOTICE_UNIT_KEYS.map((k) => (
													<SelectItem key={k} value={k}>
														{t(`deals.documents.wizard.terms.unit_${k}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
								<FormField
									control={form.control}
									name="returnTiming"
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger size="md" className="flex-1">
													<SelectValue placeholder={t('deals.documents.wizard.terms.timing_afterContractEnd')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{TIMING_KEYS.map((k) => (
													<SelectItem key={k} value={k}>
														{t(`deals.documents.wizard.terms.timing_${k}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</div>
						</FormItem>
					)}

					<div className="flex flex-col gap-2">
						<span className="text-sm leading-5 font-[500] text-(--color-foreground)">
							{t('deals.documents.wizard.terms.conditionsTitle')}
						</span>
						{RETURN_CONDITIONS.map((c) => (
							<CheckboxOption
								key={c}
								id={`return-condition-${c}`}
								label={t(`deals.documents.wizard.terms.condition_${c}`)}
								checked={conditions.includes(c)}
								onCheckedChange={(v) => toggleCondition(c, v)}
							/>
						))}
					</div>
				</section>

				{/* ── Неотделимые улучшения ──────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.improvementsTitle')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="improvementsFate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.terms.improvementsFate')}</FormLabel>
								<RadioGroup
									value={field.value}
									onValueChange={(v) => field.onChange(v as ImprovementsFate)}
									className="grid gap-2"
								>
									{IMPROVEMENTS_FATES.map((f) => (
										<RadioOption
											key={f}
											value={f}
											id={`improvements-fate-${f}`}
											bordered={false}
											label={t(`deals.documents.wizard.terms.improvementsFate_${f}`)}
										/>
									))}
								</RadioGroup>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="improvementsConsent"
						render={({ field }) => (
							<FormItem className="w-1/2 pr-1">
								<FormLabel>{t('deals.documents.wizard.terms.improvementsConsent')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder={t('deals.documents.wizard.terms.consent_written')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{CONSENT_STUBS.map((c) => (
											<SelectItem key={c.id} value={c.id}>
												{t(`deals.documents.wizard.terms.consent_${c.i18nKey}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
				</section>

				{/* ── Прочие условия ─────────────────────────────── */}
				<section className="flex flex-col gap-2">
					<SectionHeading>
						{t('deals.documents.wizard.terms.otherTitle')}
					</SectionHeading>
					<p className="text-sm leading-5 text-(--color-muted-foreground)">
						{t('deals.documents.wizard.terms.otherHint')}
					</p>
					<FormField
						control={form.control}
						name="otherConditions"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Textarea
										{...field}
										placeholder={t('deals.documents.wizard.terms.otherPlaceholder')}
										rows={4}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</section>

				{/* ── Приложения ─────────────────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.terms.attachmentsTitle')}
					</SectionHeading>
					<FileDropzone
						dropLabel={t('deals.documents.wizard.terms.dropzoneDrop')}
						selectLabel={t('deals.documents.wizard.terms.dropzoneSelect')}
						hint={t('deals.documents.wizard.terms.dropzoneHint')}
						accept=".pdf,.doc,.docx"
						multiple
						onFilesSelected={handleFilesSelected}
					/>
					{attachments.length > 0 && (
						<div className="flex flex-col gap-2">
							{attachments.map((a: TermsAttachment) => (
								<FileUploadCard
									key={a.id}
									fileName={a.name}
									fileSize={a.sizeLabel}
									state={a.state}
									onRemove={() => removeAttachment(a.id)}
									uploadedLabel={t('deals.documents.wizard.terms.attachmentUploaded')}
									actionLabel={t('deals.documents.wizard.terms.attachmentPreview')}
								/>
							))}
						</div>
					)}
				</section>
			</form>
		</Form>
	)
}
